import 'bootstrap-css-only/css/bootstrap.min.css';

import history from 'utils/history';

import PropTypes from 'prop-types';

import React from 'react';
import { isMobile as rddMobile } from 'react-device-detect';
import { connect } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { createStructuredSelector } from 'reselect';

import '@fortawesome/fontawesome-free/js/all';
import Redirect from 'components/Redirect';
import Admin from 'containers/Admin';
import Clinician from 'containers/Clinician';
import NotFoundPage from 'containers/NotFoundPage';
import Patient from 'containers/Patient';
import Referrer from 'containers/Referrer';
import ReferrerSignUp from 'containers/ReferrerSignUp';
import ReferrerVerifyEmail from 'containers/ReferrerVerifyEmail';
import SignIn from 'containers/SignIn';
import SignUp from 'containers/SignUp';
import UserForgotPassword from 'containers/UserForgotPassword';
import 'global-styles.css';
import IntakeComplete from 'pages/intake-complete';
import { getErrorMessage } from 'utils/errorUtils';
import { getLogger } from 'utils/logger';
import { resolveSignupTrack } from 'utils/registrationTracks';
import { toastOptions } from 'utils/toast';

import {
	clearDownloadError,
	generateToast,
	pingServer,
	setMobile,
	setSignupTrack,
} from './actions';
import './index.css';
import {
	makeSelectAuth,
	makeSelectDownloadError,
	makeSelectDownloadErrorMessage,
	makeSelectIsMobile,
	makeSelectServerError,
	makeSelectToastMessage,
} from './selectors';

const logger = getLogger('App');
logger.log('App started');

// The react-toastify variant methods a `msg.type` is allowed to name. This has
// to be an allowlist: `msg.type` indexes `toast` directly, and a bare
// `typeof toast[msg.type] === 'function'` test also passes for `dismiss`,
// `update`, `isActive`, `done` and every Function.prototype member, so a typo
// (or a caller reaching for an API that is not a toast variant) would get a
// live call instead of the fallback. The `typeof` check below is kept as a
// second gate because this list is not version-proof — react-toastify 4.5.2
// ships no `toast.dark`, so matching the allowlist alone would still throw.
const TOAST_VARIANTS = ['success', 'error', 'info', 'warn', 'warning', 'dark'];

// A logged-in patient followed a track-typed signup link
// (`/patient/signup/<slug>`): capture the track into client state, then hand
// off to the registration page — PatientRegister's consume/revert logic
// handles an already-started registration. Unknown slugs set no track, so a
// bad link degrades to the plain registration redirect (today's behavior).
class TrackCaptureRedirect extends React.Component {
	componentDidMount() {
		const info = resolveSignupTrack(this.props.slug);
		if (info) {
			this.props.setTrack(info.type);
		}
		history.push('/patient/registration');
	}

	render() {
		return <div />;
	}
}

TrackCaptureRedirect.propTypes = {
	slug: PropTypes.string,
	setTrack: PropTypes.func.isRequired,
};

/* eslint-disable react/prefer-stateless-function */
export class App extends React.Component {
	state = {
		serverError: false,
		handledDownloadError: false,
	};

	componentDidMount = () => {
		const { doPing } = this.props;

		window.addEventListener('resize', this.handleResize);
		this.handleResize();

		doPing();
	};

	handleResize = () => {
		const { setMobile, isMobile } = this.props;
		const newIsMobile = window.innerWidth < 1025 || rddMobile;

		if (isMobile !== newIsMobile) {
			setMobile(newIsMobile);
		}
	};

	getRoleTypeFromLocation = () => {
		if (this.props.auth && this.props.auth.isLoggedIn) {
			return `${this.props.auth.loggedInUser.roleName}`.toLowerCase();
		}
		return null;
	};

	UNSAFE_componentWillReceiveProps(nextProps) {
		const { toastMessage } = nextProps;
		const { serverError } = this.state;

		// `generateToast` normalizes to a fresh {type, text, position, options}
		// object per dispatch, so the comparison below is an identity check: it
		// fires once per dispatch and stays quiet when an unrelated prop
		// (serverError, isMobile, ...) re-runs this method. It has to work that
		// way because nothing clears the stored message — comparing by value
		// instead meant a repeated message only ever toasted once a session.
		// The string branch stays as a safety net for anything that puts
		// TOAST_GENERATED on the store without going through the action creator.
		const msg =
			typeof toastMessage === 'string' ? { text: toastMessage } : toastMessage;

		if (this.props.toastMessage !== toastMessage) {
			const isErrorType = msg != null && (msg.type === 'error' || msg.type === 'custom-error');
			const hasRenderableText =
				msg != null && typeof msg.text === 'string' && msg.text !== '';

			// error/custom-error toasts must not go silent: utils/errorUtils'
			// getErrorMessage can hand back a raw, unstringified object body
			// (a Sails validation error where `data.error` itself isn't a
			// string) instead of a string, and the text can simply be missing.
			// Dropping either here — same as any other invalid text — left an
			// admin with zero feedback after a failed operation, which reads
			// identically to success. Coerce through getErrorMessage instead:
			// it already knows how to turn arbitrary error shapes into a
			// string, and falls back to its own generic message for shapes it
			// can't parse.
			if (isErrorType && !hasRenderableText) {
				logger.error(
					`error toast had unrenderable text (type: ${msg.type}, text: ${
						msg.text === '' ? 'empty string' : typeof msg.text
					}); showing a generic error toast instead of dropping it`,
				);
			}

			const effectiveMsg =
				isErrorType && !hasRenderableText
					? { ...msg, text: getErrorMessage(msg.text) }
					: msg;

			if (
				effectiveMsg == null ||
				typeof effectiveMsg.text !== 'string' ||
				effectiveMsg.text === ''
			) {
				// react-toastify throws on undefined content ("The element you
				// provided cannot be rendered") and renders a blank box for '',
				// which reads as a UI glitch rather than a message. Either way
				// there is nothing to show, so drop it — but say so rather than
				// swallowing it, since a caller reading text off a not-yet-loaded
				// prop looks identical to no toast at all.
				if (effectiveMsg != null) {
					// Deliberately not JSON.stringify(effectiveMsg): `options` can
					// carry a React node or a cycle, and this is an unguarded
					// lifecycle method with no error boundary above it — a throw
					// from the *logging* path would unmount the app.
					logger.error(
						`dropped toast with nothing to render (type: ${effectiveMsg.type}, text: ${
							effectiveMsg.text === '' ? 'empty string' : typeof effectiveMsg.text
						})`,
					);
				}
			} else if (!effectiveMsg.type) {
				toast(effectiveMsg.text, {
					position: effectiveMsg.position || toast.POSITION.TOP_CENTER,
					autoClose: 2500,
					...effectiveMsg.options,
				});
			} else if (effectiveMsg.type === 'custom-success') {
				toast(effectiveMsg.text, {
					position: toast.POSITION.BOTTOM_LEFT,
					...toastOptions,
					...effectiveMsg.options,
				});
			} else if (effectiveMsg.type === 'custom-error') {
				toast(effectiveMsg.text, {
					position: toast.POSITION.BOTTOM_LEFT,
					...toastOptions,
					bodyClassName: 'attendance-toast-body-error',
					...effectiveMsg.options,
				});
			} else if (
				TOAST_VARIANTS.includes(effectiveMsg.type) &&
				typeof toast[effectiveMsg.type] === 'function'
			) {
				toast[effectiveMsg.type](effectiveMsg.text, {
					position: effectiveMsg.position || toast.POSITION.TOP_CENTER,
					autoClose: 2500,
					...effectiveMsg.options,
				});
			} else {
				// `effectiveMsg.type` indexes react-toastify's API directly, so a
				// typo'd type used to throw here — and nothing above App is an
				// error boundary, so React would unmount the entire tree over a
				// bad toast. Show the message with the default toast instead.
				logger.error(
					`unknown toast type "${effectiveMsg.type}"; falling back to the default toast`,
				);
				toast(effectiveMsg.text, {
					position: effectiveMsg.position || toast.POSITION.TOP_CENTER,
					autoClose: 2500,
					...effectiveMsg.options,
				});
			}
		}

		if (serverError && !nextProps.serverError) {
			this.setState({ serverError: false });
		}

		if (nextProps.serverError && !serverError) {
			const { createToast, doPing } = this.props;
			createToast({
				type: 'error',
				text: 'Server Unavailable',
				options: { bodyClassName: 'attendance-toast-body-error' },
			});

			const pingCheck = setInterval(() => {
				doPing();

				if (!this.state.serverError) {
					clearInterval(pingCheck);
					createToast({ type: 'success', text: 'Reconnected!' });
				}
			}, 5000);

			this.setState({ serverError: true });
		}

		if (nextProps.downloadError && !this.props.downloadError) {
			const { createToast } = this.props;

			createToast({
				type: 'error',
				// Surface the server's specific message (e.g. the document-readiness
				// 409 "not signed yet") when the saga captured one; fall back to the
				// generic text when it did not. Read from nextProps — this.props
				// still holds the pre-error value at this point in the lifecycle.
				text: nextProps.downloadErrorMessage || 'Could not download file',
				options: { bodyClassName: 'attendance-toast-body-error' },
			});

			clearDownloadError();
		}
	}

	getComponentFromRoleName(name) {
		let component;

		if (name === 'patient') {
			component = Patient;
		} else if (name === 'clinician') {
			component = Clinician;
		} else if (name === 'admin') {
			component = Admin;
		} else if (name === 'referrer') {
			component = Referrer;
		}

		return component;
	}

	render() {
		const roleName = this.getRoleTypeFromLocation();
		const { serverError } = this.state;

		return (
			<Router history={history}>
				<div>
					<ToastContainer hideProgressBar autoClose={5000} />
					<div
						className="form-error"
						style={{
							position: 'absolute',
							top: '5px',
							left: '10px',
							zIndex: 1021,
						}}
					>
						{serverError && 'Server unavailable'}
					</div>
					{roleName && (
						<Switch>
							<Route
								exact
								path="/intake-complete"
								component={IntakeComplete}
							/>
							{/* Track-typed signup links also reach patients who are
							    already logged in; without this the generic redirect to
							    /patient/registration would silently drop the track. */}
							{roleName === 'patient' && (
								<Route
									exact
									path="/patient/signup/:track"
									render={({ match }) => (
										<TrackCaptureRedirect
											slug={match.params.track}
											setTrack={this.props.setTrack}
										/>
									)}
								/>
							)}
							<Route
								path="/"
								component={this.getComponentFromRoleName(roleName)}
							/>
							<Route component={NotFoundPage} />
						</Switch>
					)}
					{!roleName && (
						<Switch>
							<Route
								exact
								path="/intake-complete"
								component={IntakeComplete}
							/>
							<Route exact path="/admin/signin" component={SignIn} />
							<Route exact path="/admin/signup" component={SignUp} />
							<Route
								path="/admin"
								component={() => <Redirect redirectPath="/admin/signin" />}
							/>
							<Route exact path="/clinician/signin" component={SignIn} />
							<Route exact path="/clinician/signup" component={SignUp} />
							<Route
								path="/clinician"
								component={() => <Redirect redirectPath="/clinician/signin" />}
							/>
							<Route exact path="/patient/signin" component={SignIn} />
							<Route exact path="/patient/signup" component={SignUp} />
							{/* Track-typed signup links: /patient/signup/<track> presets the
							    registration type. This matches any slug; SignUp resolves an
							    unknown one to no track and degrades to the chooser flow. */}
							<Route
								exact
								path="/patient/signup/:track"
								component={SignUp}
							/>
							<Route
								path="/patient"
								component={() => <Redirect redirectPath="/patient/signin" />}
							/>
							<Route exact path="/referrer/verify-email" component={ReferrerVerifyEmail} />
						<Route exact path="/referrer/signup" component={ReferrerSignUp} />
							<Route exact path="/referrer/signin" component={SignIn} />
							<Route
								path="/referrer"
								component={() => <Redirect redirectPath="/referrer/signin" />}
							/>
							<Route
								exact
								path="/"
								component={() => <Redirect redirectPath="/signin" />}
							/>
							<Route exact path="/signin" component={SignIn}></Route>
							<Route
								path="/user-forgot-password"
								component={UserForgotPassword}
							/>
							<Route component={NotFoundPage} />
						</Switch>
					)}
				</div>
			</Router>
		);
	}
}

App.propTypes = {
	auth: PropTypes.object.isRequired,
	// `generateToast` stores the object shape; the bare string is still accepted
	// for anything that dispatches TOAST_GENERATED without the action creator.
	toastMessage: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			type: PropTypes.string,
			// String, not node: the handler drops (and logs) anything that is not
			// a non-empty string, so declaring `node` here would promise a shape
			// that never reaches the toaster.
			text: PropTypes.string,
			position: PropTypes.string,
			options: PropTypes.object,
		}),
	]),
	// The server's specific download-failure message (blob→JSON parsed in the
	// download saga); empty when none, in which case the toast uses generic text.
	downloadErrorMessage: PropTypes.string,
	setTrack: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
	auth: makeSelectAuth(),
	toastMessage: makeSelectToastMessage(),
	serverError: makeSelectServerError(),
	isMobile: makeSelectIsMobile(),
	downloadError: makeSelectDownloadError(),
	downloadErrorMessage: makeSelectDownloadErrorMessage(),
});

function mapDispatchToProps(dispatch) {
	return {
		dispatch,
		doPing: () => dispatch(pingServer()),
		createToast: (msg) => dispatch(generateToast(msg)),
		setMobile: (value) => dispatch(setMobile(value)),
		clearDownloadError: () => dispatch(clearDownloadError()),
		setTrack: (track) => dispatch(setSignupTrack(track)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
